// Shared types + pure mappers for the report/live "Sound used" + "Status"
// columns (Official Audio Compliance + Post Liveness). The backend
// (ListCreatorContent) supplies sound_used / campaign_has_official_audio /
// liveness_state per row; these mappers turn that into the discrete cell states
// the Figma components render. Pure + framework-free so they're unit-testable.

// The wire types live in @discovr/types (authoritative backend contract);
// re-exported here so the cells/table keep importing from one place.
import type { AudioVerdict, SoundUsed, LivenessState, OfficialAudio } from '@discovr/types';
export type { AudioVerdict, SoundUsed, LivenessState };

/** The row fields the two report cells read. */
export interface AudioLivenessRow {
  sound_used?: SoundUsed | null;
  campaign_has_official_audio?: boolean;
  liveness_state?: LivenessState;
}

// ── Sound used ───────────────────────────────────────────────────────────────

/** Discrete states of the SOUND USED cell (Figma 3966-59108). */
export type SoundUsedState =
  | 'campaign' // OFFICIAL_AUDIO — matched the campaign sound
  | 'different' // ORIGINAL_AUDIO or DIFFERENT_AUDIO — not the campaign sound
  | 'checking' // official audio set, but no verdict yet
  | 'couldnt_check' // COULD_NOT_DETERMINE
  | 'none'; // campaign has no official audio (or non-IG) → em-dash

/**
 * Map a row to its SOUND USED cell state. Verdict wins when present; otherwise
 * "checking" iff the campaign expects a sound but none is recorded yet; else
 * "none".
 */
export function soundUsedState(row: AudioLivenessRow): SoundUsedState {
  switch (row.sound_used?.verdict) {
    case 'OFFICIAL_AUDIO':
      return 'campaign';
    case 'ORIGINAL_AUDIO':
    case 'DIFFERENT_AUDIO':
      return 'different';
    case 'COULD_NOT_DETERMINE':
      return 'couldnt_check';
    default:
      // A present sound_used with an unrecognized/missing verdict means a check
      // ran but couldn't be classified (a partial write, or a verdict value the
      // FE union hasn't caught up to) — show "couldn't check", never hide it as —.
      if (row.sound_used) return 'couldnt_check';
      return row.campaign_has_official_audio ? 'checking' : 'none';
  }
}

/** Campaign-level gate for the SOUND USED column, mirroring the BE's
 *  `campaign_has_official_audio` (`len(audio_ids) > 0`). */
export function campaignHasOfficialAudio(officialAudio?: OfficialAudio | null): boolean {
  return (officialAudio?.audio_ids?.length ?? 0) > 0;
}

// ── Status (liveness) ────────────────────────────────────────────────────────

/** Discrete states of the STATUS cell (Figma 3966-57906). */
export type StatusState = 'live' | 'not_visible' | 'ended' | 'unknown';

/**
 * Map campaign running-state + liveness to the STATUS cell. ENDED is a UI
 * derivation (campaign no longer running) — it is NOT a backend liveness state.
 * "Not publicly visible" is the only language for a missing post — never
 * "deleted", never attributing removal to the creator.
 */
export function statusState(campaignRunning: boolean, liveness?: LivenessState): StatusState {
  if (!campaignRunning) return 'ended';
  if (liveness === 'NOT_VISIBLE') return 'not_visible';
  if (liveness === 'LIVE') return 'live';
  // Nothing has checked this post yet, so no claim can be made. Defaulting to
  // 'live' here would paint a confident green pill on a post no check has ever
  // observed — the same false confidence the sound cell avoids with
  // 'couldnt_check'.
  return 'unknown';
}

/** Campaign statuses that mean "still running", matching the rest of the app
 *  (see BrandCampaignsTab). 'running' alone would render a live or active
 *  campaign's rows as ENDED, inverting the column's meaning. */
export function isCampaignRunning(status?: string): boolean {
  const s = (status ?? 'running').toLowerCase();
  return s === 'running' || s === 'live' || s === 'active';
}
